import { useState, useMemo } from 'react';
import { X, FileDown, FileSpreadsheet, Bot } from 'lucide-react';
import type { PageResult } from '../../types';
import {
  ALL_SECTIONS,
  SECTION_LABELS,
  getSectionCounts,
  getClaudeRowCount,
  exportIssuesCsv,
  exportIssuesXlsx,
  exportClaudeAnalysisCsv,
  type ExportSection,
} from '../../utils/exportIssuesCsv';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageResults: PageResult[];
  siteUrl: string;
}

type ExportFormat = 'xlsx' | 'csv' | 'claude';

export function ExportModal({ isOpen, onClose, pageResults, siteUrl }: ExportModalProps) {
  const sectionCounts = useMemo(() => getSectionCounts(pageResults), [pageResults]);
  const claudeRowCount = useMemo(() => getClaudeRowCount(pageResults), [pageResults]);

  const defaultSelected = useMemo(
    () => ALL_SECTIONS.filter((s) => sectionCounts[s] > 0),
    [sectionCounts],
  );

  const [selected, setSelected] = useState<ExportSection[]>(defaultSelected);
  const [format, setFormat] = useState<ExportFormat>('xlsx');

  if (!isOpen) return null;

  const toggle = (section: ExportSection) => {
    setSelected((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section],
    );
  };

  const isClaudeMode = format === 'claude';
  const totalRows = isClaudeMode
    ? claudeRowCount
    : selected.reduce((sum, s) => sum + sectionCounts[s], 0);

  const handleDownload = () => {
    if (format === 'xlsx') {
      exportIssuesXlsx(pageResults, siteUrl, selected);
    } else if (format === 'csv') {
      exportIssuesCsv(pageResults, siteUrl, selected);
    } else {
      exportClaudeAnalysisCsv(pageResults, siteUrl, selected);
    }
    onClose();
  };

  const canDownload = isClaudeMode ? true : selected.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-primary/80" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-light">
          <h2 className="text-heading-3 font-semibold">Download Issues</h2>
          <button onClick={onClose} className="p-2 text-gray-medium hover:text-dark-text rounded-card transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Format */}
          <div className="space-y-2">
            <h3 className="text-body font-semibold text-gray-dark">Format</h3>
            <div className="space-y-2">
              {/* Excel */}
              <label className={`flex items-center gap-3 p-3 rounded-card border cursor-pointer transition-colors ${format === 'xlsx' ? 'border-coral bg-coral/5' : 'border-gray-light hover:border-coral'}`}>
                <input type="radio" name="format" checked={format === 'xlsx'} onChange={() => setFormat('xlsx')} className="h-4 w-4 text-coral focus:ring-coral" />
                <div className="flex items-center gap-2 flex-1">
                  <FileSpreadsheet size={16} className="text-status-success flex-shrink-0" />
                  <div>
                    <div className="font-medium text-body-sm">Excel (.xlsx)</div>
                    <div className="text-caption text-gray-medium">Full detail — one tab per section</div>
                  </div>
                </div>
              </label>

              {/* CSV */}
              <label className={`flex items-center gap-3 p-3 rounded-card border cursor-pointer transition-colors ${format === 'csv' ? 'border-coral bg-coral/5' : 'border-gray-light hover:border-coral'}`}>
                <input type="radio" name="format" checked={format === 'csv'} onChange={() => setFormat('csv')} className="h-4 w-4 text-coral focus:ring-coral" />
                <div className="flex items-center gap-2 flex-1">
                  <FileDown size={16} className="text-gray-medium flex-shrink-0" />
                  <div>
                    <div className="font-medium text-body-sm">CSV (.csv)</div>
                    <div className="text-caption text-gray-medium">Full detail — single flat file</div>
                  </div>
                </div>
              </label>

              {/* Claude Analysis */}
              <label className={`flex items-center gap-3 p-3 rounded-card border cursor-pointer transition-colors ${format === 'claude' ? 'border-coral bg-coral/5' : 'border-gray-light hover:border-coral'}`}>
                <input type="radio" name="format" checked={format === 'claude'} onChange={() => setFormat('claude')} className="h-4 w-4 text-coral focus:ring-coral" />
                <div className="flex items-center gap-2 flex-1">
                  <Bot size={16} className="text-coral flex-shrink-0" />
                  <div>
                    <div className="font-medium text-body-sm">Claude Analysis (.csv)</div>
                    <div className="text-caption text-gray-medium">Deduplicated by issue type — optimised for AI analysis</div>
                  </div>
                </div>
              </label>

              {isClaudeMode && (
                <div className="p-3 bg-coral/5 border border-coral/20 rounded-card text-caption text-gray-dark">
                  Issues are grouped by type with page counts and one example element each.
                  Strips repeated DOM nodes so the file stays small enough for Claude's context window.
                </div>
              )}
            </div>
          </div>

          {/* Sections — hidden in Claude mode as it always covers all sections */}
          {!isClaudeMode && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-body font-semibold text-gray-dark">Sections</h3>
                <div className="flex gap-3 text-body-sm">
                  <button onClick={() => setSelected(ALL_SECTIONS.filter((s) => sectionCounts[s] > 0))} className="text-coral hover:underline">All</button>
                  <button onClick={() => setSelected([])} className="text-gray-medium hover:underline">None</button>
                </div>
              </div>
              <div className="space-y-1">
                {ALL_SECTIONS.map((section) => {
                  const count = sectionCounts[section];
                  const isChecked = selected.includes(section);
                  const isEmpty = count === 0;
                  return (
                    <label
                      key={section}
                      className={`flex items-center gap-3 p-3 rounded-card border transition-colors ${isEmpty ? 'opacity-40 cursor-not-allowed border-gray-light' : isChecked ? 'border-coral bg-coral/5 cursor-pointer' : 'border-gray-light hover:border-coral cursor-pointer'}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isEmpty}
                        onChange={() => toggle(section)}
                        className="h-4 w-4 text-coral rounded focus:ring-coral"
                      />
                      <span className="flex-1 font-medium text-body-sm">{SECTION_LABELS[section]}</span>
                      <span className={`text-caption px-2 py-0.5 rounded-full ${isEmpty ? 'bg-gray-light text-gray-medium' : 'bg-coral/10 text-coral'}`}>
                        {count.toLocaleString()} {count === 1 ? 'row' : 'rows'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-light">
          <span className="text-body-sm text-gray-medium">
            {totalRows.toLocaleString()} {isClaudeMode ? 'unique issues' : 'total rows'}
          </span>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button
              onClick={handleDownload}
              disabled={!canDownload}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {format === 'xlsx' ? <FileSpreadsheet size={16} /> : format === 'claude' ? <Bot size={16} /> : <FileDown size={16} />}
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
